import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  CurrencyDollar, TrendUp, Users, ChalkboardTeacher, ClipboardText,
  FilePdf, Star, ChartLine,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { cn } from "@/lib/utils";

const fmt$ = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function Reports() {
  const { t } = useTranslation();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/reports/monthly", { params: { month } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const exportPDF = () => {
    if (!data) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const teacherRows = data.top_teachers.map((t, i) =>
      `<tr><td>${i + 1}</td><td>${t.full_name}</td><td>${t.subject || ""}</td><td>${t.avg_rating}★</td><td>${t.classes}</td></tr>`
    ).join("");
    const studentRows = data.top_students.map((s, i) =>
      `<tr><td>${i + 1}</td><td>${s.full_name}</td><td>${s.english_level || ""}</td><td>${s.attendance_rate}%</td></tr>`
    ).join("");
    const methodRows = Object.entries(data.revenue.by_method).map(([k, v]) =>
      `<tr><td>${k}</td><td>$${v.toFixed(2)}</td></tr>`).join("");
    w.document.write(`<html><head><title>Bilinguist — ${data.period}</title>
      <style>
        @page { margin: 24mm 16mm; }
        body { font-family: system-ui, -apple-system, sans-serif; color: #0A1128; }
        header { border-bottom: 3px solid #E63946; padding-bottom: 16px; margin-bottom: 24px; display: flex; align-items: center; justify-content: space-between; }
        .brand { font-size: 24px; font-weight: 800; letter-spacing: -0.5px; }
        .subtitle { text-transform: uppercase; letter-spacing: 3px; font-size: 10px; color: #64748b; }
        h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 2px; color: #64748b; margin: 32px 0 12px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; }
        .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 24px 0; }
        .kpi { border: 1px solid #e2e8f0; padding: 14px; border-radius: 8px; }
        .kpi-lbl { text-transform: uppercase; font-size: 10px; letter-spacing: 2px; color: #64748b; }
        .kpi-val { font-size: 22px; font-weight: 800; margin-top: 4px; font-family: 'IBM Plex Mono', ui-monospace, monospace; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 8px; }
        th { background: #0A1128; color: #fff; padding: 8px; text-align: left; text-transform: uppercase; font-size: 10px; letter-spacing: 1.5px; }
        td { border-bottom: 1px solid #e2e8f0; padding: 8px; }
        .footer { margin-top: 40px; font-size: 10px; color: #64748b; text-align: center; }
      </style></head><body>
      <header>
        <div>
          <div class="subtitle">Bilinguist School OS</div>
          <div class="brand">Monthly Report — ${data.period}</div>
        </div>
        <div style="text-align:right;font-size:11px;color:#64748b">
          Generated ${new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}<br />
          ${data.start} → ${data.end}
        </div>
      </header>

      <h2>Executive Summary</h2>
      <div class="kpis">
        <div class="kpi"><div class="kpi-lbl">Revenue</div><div class="kpi-val">$${data.revenue.total.toFixed(0)}</div></div>
        <div class="kpi"><div class="kpi-lbl">Outstanding</div><div class="kpi-val" style="color:#E63946">$${data.revenue.outstanding.toFixed(0)}</div></div>
        <div class="kpi"><div class="kpi-lbl">Attendance</div><div class="kpi-val">${data.attendance.attendance_rate}%</div></div>
        <div class="kpi"><div class="kpi-lbl">New Students</div><div class="kpi-val">${data.students.new_registrations}</div></div>
      </div>

      <h2>Revenue by Method</h2>
      <table><thead><tr><th>Method</th><th>Amount</th></tr></thead><tbody>${methodRows}</tbody></table>

      <h2>Top Teachers by Rating</h2>
      <table><thead><tr><th>#</th><th>Name</th><th>Subject</th><th>Rating</th><th>Classes</th></tr></thead><tbody>${teacherRows}</tbody></table>

      <h2>Top Students by Attendance</h2>
      <table><thead><tr><th>#</th><th>Name</th><th>Level</th><th>Attendance</th></tr></thead><tbody>${studentRows}</tbody></table>

      <h2>Attendance Breakdown</h2>
      <table><thead><tr><th>Status</th><th>Count</th></tr></thead><tbody>
        ${Object.entries(data.attendance.summary).map(([k, v]) => `<tr><td style="text-transform:capitalize">${k}</td><td>${v}</td></tr>`).join("")}
        <tr style="font-weight:bold"><td>Total</td><td>${data.attendance.total}</td></tr>
      </tbody></table>

      <div class="footer">Bilinguist School Management System · Confidential — for internal use only</div>
      <script>window.print()</script>
    </body></html>`);
    w.document.close();
    toast.success(t("reports.openingPdf"));
  };

  const methodData = data ? Object.entries(data.revenue.by_method).map(([k, v]) => ({ name: k, value: v })) : [];
  const attData = data ? Object.entries(data.attendance.summary).map(([k, v]) => ({ name: k, value: v })) : [];
  const PIE = ["hsl(var(--chart-1))", "hsl(var(--destructive))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("nav.reports")}</div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("reports.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("reports.subtitle")}</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">{t("reports.period")}</Label>
            <Input data-testid="report-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1.5 h-10 w-44" />
          </div>
          <Button data-testid="report-export-pdf" onClick={exportPDF} disabled={!data} className="rounded-full gap-2 h-10">
            <FilePdf size={16} weight="duotone" /> {t("reports.exportPdf")}
          </Button>
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground">{t("reports.loading")}</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: "revenue", label: t("reports.kpi.revenue"), value: fmt$(data.revenue.total), icon: CurrencyDollar, tone: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
              { key: "outstanding", label: t("reports.kpi.outstanding"), value: fmt$(data.revenue.outstanding), icon: TrendUp, tone: "bg-destructive/10 text-destructive" },
              { key: "attendance", label: t("reports.kpi.attendance"), value: `${data.attendance.attendance_rate}%`, icon: ClipboardText, tone: "bg-primary/10 text-primary" },
              { key: "new", label: t("reports.kpi.newStudents"), value: data.students.new_registrations, icon: Users, tone: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
            ].map((k) => (
              <Card key={k.key} className="border-border/60" data-testid={`report-kpi-${k.key}`}>
                <CardContent className="p-5">
                  <div className={cn("w-10 h-10 rounded-md flex items-center justify-center", k.tone)}>
                    <k.icon size={20} weight="duotone" />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mt-4">{k.label}</div>
                  <div className="text-3xl font-bold font-mono mt-1">{k.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-border/60"><CardContent className="p-5">
              <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>{t("reports.revenueByMethod")}</h3>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                  <BarChart data={methodData}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="value" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent></Card>

            <Card className="border-border/60"><CardContent className="p-5">
              <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>{t("reports.attendanceBreakdown")}</h3>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                  <PieChart>
                    <Pie data={attData} innerRadius={45} outerRadius={80} dataKey="value" paddingAngle={3}>
                      {attData.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent></Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-border/60"><CardContent className="p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                <ChalkboardTeacher size={16} weight="duotone" /> {t("reports.topTeachers")}
              </h3>
              <ul className="divide-y divide-border">
                {data.top_teachers.map((tch, i) => (
                  <li key={tch.id} className="py-3 flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{tch.full_name}</div>
                      <div className="text-xs text-muted-foreground">{tch.subject} · {t("reports.classesCount", { count: tch.classes })}</div>
                    </div>
                    <div className="flex items-center gap-1 font-mono font-bold text-sm">
                      {tch.avg_rating}<Star size={12} weight="fill" className="text-amber-500" />
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent></Card>

            <Card className="border-border/60"><CardContent className="p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                <Users size={16} weight="duotone" /> {t("reports.topStudents")}
              </h3>
              <ul className="divide-y divide-border">
                {data.top_students.map((s, i) => (
                  <li key={s.id} className="py-3 flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{s.full_name}</div>
                      <div className="text-xs text-muted-foreground">{t("reports.level", { level: s.english_level || "—" })}</div>
                    </div>
                    <Badge variant="secondary" className="font-mono">{s.attendance_rate}%</Badge>
                  </li>
                ))}
                {data.top_students.length === 0 && <li className="py-6 text-sm text-muted-foreground text-center">{t("reports.noAttendanceData")}</li>}
              </ul>
            </CardContent></Card>
          </div>
        </>
      )}
    </div>
  );
}
